import { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Mail, ArrowRight, Lock, AlertCircle } from "lucide-react";
import { login, ApiError } from "@/lib/api";
import { useAuth, AUTH_QUERY_KEY } from "@/hooks/useAuth";

/**
 * Connexion **admin par mot de passe** (usage solo — remplace le magic-link OTP). Formulaire en une
 * étape : email + mot de passe → `POST /api/auth/login` → session ouverte, redirection.
 */
export function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { user } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();

  const destination =
    (location.state as { from?: { pathname?: string } } | null)?.from?.pathname ?? "/prospects";

  useEffect(() => {
    if (user) {
      navigate(destination, { replace: true });
    }
  }, [user, navigate, destination]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password) return;
    setError(null);
    setSubmitting(true);
    try {
      const authUser = await login(email.trim(), password);
      // Affichage instantané, puis on resynchronise via `GET /api/auth/me` : ce GET protégé amorce
      // aussi le cookie `csrf_token` (posé par le backend sur les requêtes sûres du groupe protégé)
      // avant toute mutation ultérieure.
      queryClient.setQueryData(AUTH_QUERY_KEY, authUser);
      queryClient.invalidateQueries({ queryKey: AUTH_QUERY_KEY });
      navigate(destination, { replace: true });
    } catch (err) {
      let message = "Échec de la connexion. Réessayez dans un instant.";
      if (err instanceof ApiError) {
        if (err.status === 401) message = "Email ou mot de passe invalide.";
        else if (err.status === 423)
          message = "Trop de tentatives. Réessayez dans quelques minutes.";
      }
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-background">
      {/* Bandeau hero gradient navy/blue (style site Aurora) */}
      <div className="aurora-hero-gradient flex h-32 items-center justify-center px-6">
        <div className="flex items-center justify-center rounded-lg bg-white px-4 py-2 shadow-lg">
          <img src="/logo-aurora.png" alt="Aurora Digital Solutions" className="h-10 w-auto" />
        </div>
      </div>

      {/* Carte de login centrée */}
      <div className="flex flex-1 items-start justify-center px-6 pt-16">
        <div className="w-full max-w-sm">
          <h1 className="mb-2 text-2xl font-bold text-aurora-navy">Connexion</h1>
          <p className="mb-8 text-sm text-neutral-600">
            Plateforme interne Aurora — accès réservé à l'administrateur.
          </p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-aurora-navy">
                Adresse e-mail
              </label>
              <div className="relative">
                <Mail className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-neutral-400" />
                <input
                  id="email"
                  type="email"
                  autoComplete="username"
                  required
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="vous@aurora-ds.com"
                  className="block w-full rounded-md border border-neutral-300 bg-white py-2.5 pl-10 pr-3 text-sm placeholder:text-neutral-400 focus:border-aurora-bright-blue focus:outline-none focus:ring-2 focus:ring-[color-mix(in_srgb,var(--aurora-color-border-focus)_20%,transparent)]"
                  disabled={submitting}
                />
              </div>
            </div>

            <div>
              <label
                htmlFor="password"
                className="mb-1.5 block text-sm font-medium text-aurora-navy"
              >
                Mot de passe
              </label>
              <div className="relative">
                <Lock className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-neutral-400" />
                <input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="block w-full rounded-md border border-neutral-300 bg-white py-2.5 pl-10 pr-3 text-sm placeholder:text-neutral-400 focus:border-aurora-bright-blue focus:outline-none focus:ring-2 focus:ring-[color-mix(in_srgb,var(--aurora-color-border-focus)_20%,transparent)]"
                  disabled={submitting}
                />
              </div>
            </div>

            {error && (
              <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
                <AlertCircle className="mt-0.5 size-4 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <button
              type="submit"
              className="btn-aurora-primary w-full justify-center py-3 text-base"
              disabled={submitting}
            >
              {submitting ? "Connexion…" : "Se connecter"}
              <ArrowRight className="size-4" />
            </button>
          </form>

          <div className="mt-12 flex items-center justify-center gap-4 text-xs text-neutral-500">
            <Link to="/privacy" className="hover:text-aurora-orange">
              Politique de confidentialité
            </Link>
            <span>·</span>
            <a
              href="https://www.aurora-ds.com"
              target="_blank"
              rel="noreferrer"
              className="hover:text-aurora-orange"
            >
              aurora-ds.com
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
